import React from "react";

// Configuration flags
// Set to false for faster builds (development mode)
// Set to true for optimized chunks (production mode)
const useCodeSplitting = false;



// Toggle flags to control which module sources to import
// Setting these to false will prevent importing from those sources entirely
const importPackageModules = true;  // Set to false to disable node_modules imports
const importYalcModules = true;     // Set to false to disable .yalc imports

// Helper to extract component name from a file path
const getComponentName = (path) => {
  const match = path.match(/\/([^\/]+)\.tsx$/);
  return match ? match[1] : null;
};

// First, eagerly import all local project components
const localProjectModules = import.meta.glob('/frontend/src/stories/**/!(Layout)/**/*.tsx', { eager: true });

// Create a registry of local component names
const localComponentRegistry = new Set();

// Register all components from the local project
Object.keys(localProjectModules).forEach(path => {
  if (!path.includes('.stories')) {
    const componentName = getComponentName(path);
    if (componentName) {
      localComponentRegistry.add(componentName);
    }
  }
});

// Only import package modules if enabled
let packageModules = {};
if (importPackageModules) {
  packageModules = import.meta.glob('/node_modules/@mindarc/arctheme-components/frontend/src/stories/components/**/!(Layout)/**/*.tsx', { eager: false });
}

// Only import yalc modules if enabled
let yalcModules = {};
if (importYalcModules) {
  yalcModules = import.meta.glob('/.yalc/@mindarc/arctheme-components/frontend/src/stories/components/**/!(Layout)/**/*.tsx', { eager: false });
}

// Filter out components that already exist in local project
const filteredPackageModules = {};
Object.keys(packageModules).forEach(path => {
  if (!path.includes('.stories')) {
    const componentName = getComponentName(path);
    if (componentName && !localComponentRegistry.has(componentName)) {
      filteredPackageModules[path] = packageModules[path];
    }
  }
});

// Filter out yalc components that already exist in local project
const filteredYalcModules = {};
Object.keys(yalcModules).forEach(path => {
  if (!path.includes('.stories')) {
    const componentName = getComponentName(path);
    if (componentName && !localComponentRegistry.has(componentName)) {
      filteredYalcModules[path] = yalcModules[path];
    }
  }
});

// Process components from any module source
const processModules = (modules, source = 'unknown') => {
  const result = {};
  
  Object.keys(modules).forEach(path => {
    // Skip story files
    if (path.includes('.stories')) return;
    
    // Get component name
    const componentName = getComponentName(path);
    if (!componentName) return;
    
    // Process the component based on code splitting configuration
    try {
      if (useCodeSplitting) {
        // For code splitting mode: Use React.lazy
        result[componentName] = React.lazy(() => 
          modules[path]().then(module => {
            const Component = module[componentName] || module.default;
            return { default: Component };
          })
        );
      } else {
        // For eager loading mode: Access directly
        const module = modules[path];
        const Component = module[componentName] || module.default;
        result[componentName] = Component;
      }
    } catch (e) {
      console.error(`[LazyComponent] Failed to load ${source} component ${componentName} from ${path}:`, e);
    }
  });
  
  return result;
};

// Process all module sources
const processedLocalComponents = processModules(localProjectModules, 'local');
const processedPackageComponents = processModules(filteredPackageModules, 'package');
const processedYalcComponents = processModules(filteredYalcModules, 'yalc');

// Merge components in the correct priority order
const lazyComponents = {
  ...processedPackageComponents,
  ...processedYalcComponents,
  ...processedLocalComponents,
};

export { lazyComponents, localComponentRegistry };